import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { linkWalletWithProof, isWalletLimitError } from '../utils/walletAuth';
import api from '../utils/api';

function verifyNftHoldings(account) {
  const addr = String(account || '').toLowerCase();
  if (!/^0x[a-f0-9]{40}$/.test(addr)) return;
  api.get('/tickets/verify-holdings', { params: { walletAddress: addr } }).catch(() => {});
}

/**
 * When a logged-in user connects a wallet, prove ownership and link it (once).
 */
export default function WalletAccountSync() {
  const { user, refreshUser } = useAuth();
  const { account } = useWallet();
  const lastHandled = useRef(null);
  const linkInFlight = useRef(false);

  useEffect(() => {
    if (!user?._id) {
      lastHandled.current = null;
      linkInFlight.current = false;
    }
  }, [user?._id]);

  useEffect(() => {
    if (!user?._id || !account) return;

    const addr = String(account).toLowerCase();
    const key = `${user._id}:${addr}`;
    if (lastHandled.current === key) return;

    const linked = new Set(
      [...(user.wallets || []), user.walletAddress].filter(Boolean).map((w) => String(w).toLowerCase())
    );

    if (linked.has(addr)) {
      lastHandled.current = key;
      verifyNftHoldings(addr);
      return;
    }

    if (linkInFlight.current) return;
    linkInFlight.current = true;

    linkWalletWithProof(account)
      .then((res) => {
        lastHandled.current = key;
        if (res.data?.user) refreshUser(res.data.user);
        else refreshUser();
        verifyNftHoldings(addr);
      })
      .catch((err) => {
        if (isWalletLimitError(err)) {
          // Keep lastHandled unset so they can retry after unlinking;
          // modal is shown via app:wallet-limit event.
          return;
        }
        console.warn('[WalletAccountSync] link failed:', err?.response?.data?.message || err.message);
        // Don't spam re-prompts on user rejecting the signature
        if (
          String(err?.message || '')
            .toLowerCase()
            .includes('reject') ||
          err?.code === 4001
        ) {
          lastHandled.current = key;
        }
      })
      .finally(() => {
        linkInFlight.current = false;
      });
  }, [user?._id, user?.wallets, user?.walletAddress, account, refreshUser]);

  return null;
}
